import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    TextField,
    InputAdornment,
    IconButton,
    Stack,
    Alert,
    Divider
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Add as AddIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import useDictation from '../../../hooks/useDictation';
import { generateUUID } from '../../../utils/uuid';

export default function HazardActionsModal({
    open,
    onClose,
    hazard,
    workActivity,
    energyName,
    onConfirm
}) {
    // Custom hazard name (for new custom hazards)
    const [customHazardName, setCustomHazardName] = useState('');

    // Actions state
    const [selectedActionIds, setSelectedActionIds] = useState(new Set());
    const [customActions, setCustomActions] = useState([]);
    const [customActionText, setCustomActionText] = useState('');

    // Dictation
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });
    const currentFieldRef = useRef(null);
    const [activeField, setActiveField] = useState(null);

    const customHazardInputRef = useRef(null);
    const customActionInputRef = useRef(null);

    // Determine if this is a new custom hazard (needs name input)
    const isNewCustomHazard = hazard?.isNewCustom === true;
    // Determine if this is edit mode
    const isEditMode = hazard?._editIndex !== undefined;
    const hazardName = isNewCustomHazard ? customHazardName : (hazard?.value || hazard?.name || 'Hazard');

    // Reset state when modal opens with new hazard
    useEffect(() => {
        if (open && hazard) {
            setCustomActionText('');
            setActiveField(null);

            if (isEditMode) {
                // Edit mode: pre-fill with existing selected actions
                const selectedActions = hazard._selectedPreventHazards || hazard.preventHazards || [];

                // Separate predefined vs custom actions
                const predefinedIds = new Set();
                const customList = [];

                selectedActions.forEach(action => {
                    if (typeof action === 'string') {
                        // String action - treat as custom
                        customList.push({ id: generateUUID(), value: action, isCustom: true });
                    } else if (action.isCustom) {
                        // Custom action object
                        customList.push({ ...action, id: action.id || generateUUID() });
                    } else if (action.id) {
                        // Predefined action - mark as selected
                        predefinedIds.add(action.id);
                    }
                });

                setCustomActions(customList);
                // Create selection set with both predefined and custom action ids
                const allSelectedIds = new Set([...predefinedIds, ...customList.map(a => a.id)]);
                setSelectedActionIds(allSelectedIds);
                setCustomHazardName('');
            } else {
                // New hazard mode
                setSelectedActionIds(new Set());
                setCustomActions([]);
                setCustomHazardName('');
            }

            // Focus on hazard name input if new custom hazard
            if (hazard?.isNewCustom) {
                setTimeout(() => customHazardInputRef.current?.focus(), 100);
            }
        }
    }, [open, hazard?.id, hazard?.isNewCustom, hazard?._editIndex]);

    // In edit mode, use all available actions; otherwise use hazard's preventHazards
    // Список готовых действий показываем по алфавиту (просьба безопасности);
    // свои действия остаются в порядке добавления, ниже готовых.
    const preventHazards = [...(isEditMode
        ? (hazard?._allPreventHazards || hazard?.preventHazards || [])
        : (hazard?.preventHazards || []))]
        .sort((a, b) => (a?.value || a?.name || '').localeCompare(b?.value || b?.name || '', 'en', { sensitivity: 'base' }));

    // Combined list: predefined + custom actions
    const allActions = [
        ...preventHazards.map(a => ({ ...a, isCustom: false })),
        ...customActions
    ];

    // Validation - must have at least one action SELECTED (checked)
    const hasValidHazardName = isNewCustomHazard ? customHazardName.trim().length > 0 : true;
    const hasSelectedActions = selectedActionIds.size > 0;
    const isValid = hasValidHazardName && hasSelectedActions;

    // Dictation toggle
    const toggleDictation = useCallback(async (field) => {
        if (!dictationSupported) return;

        const fieldIsActive = isListening && activeField === field;
        if (fieldIsActive) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }

        currentFieldRef.current = field;
        setActiveField(field);

        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !currentFieldRef.current) return;
                const key = currentFieldRef.current;

                if (key === 'hazardName') {
                    setCustomHazardName(prev => ((prev || '') + (prev ? ' ' : '') + transcript).trim());
                } else if (key === 'actionText') {
                    setCustomActionText(prev => ((prev || '') + (prev ? ' ' : '') + transcript).trim());
                }
            },
            onEnd: () => {
                setActiveField(null);
                currentFieldRef.current = null;
            },
            onError: () => {
                setActiveField(null);
                currentFieldRef.current = null;
            },
        });
    }, [dictationSupported, isListening, activeField, startDictation, stopDictation]);

    const handleActionToggle = (actionId) => {
        setSelectedActionIds(prev => {
            const next = new Set(prev);
            if (next.has(actionId)) {
                next.delete(actionId);
            } else {
                next.add(actionId);
            }
            return next;
        });
    };

    const handleAddCustomAction = () => {
        const trimmed = customActionText.trim();
        if (!trimmed) return;

        const newAction = { id: generateUUID(), value: trimmed, isCustom: true };
        setCustomActions(prev => [...prev, newAction]);
        // Auto-select the newly added action
        setSelectedActionIds(prev => new Set([...prev, newAction.id]));
        setCustomActionText('');
        customActionInputRef.current?.focus();
    };

    const handleRemoveCustomAction = (id) => {
        setCustomActions(prev => prev.filter(a => a.id !== id));
        setSelectedActionIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const handleKeyPress = (e, action) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (action === 'addAction') {
                handleAddCustomAction();
            }
        }
    };

    const handleConfirm = () => {
        if (!isValid) return;

        const selectedFromList = preventHazards.filter(a => selectedActionIds.has(a.id));
        const selectedCustom = customActions.filter(a => selectedActionIds.has(a.id));
        const finalActions = [...selectedFromList, ...selectedCustom];

        // Build final hazard object
        const finalHazard = isNewCustomHazard
            ? {
                id: `custom-${Date.now()}`,
                value: customHazardName.trim(),
                name: customHazardName.trim(),
                preventHazards: [],
                isCustom: true
            }
            : hazard;

        onConfirm(finalHazard, finalActions);
    };

    const handleClose = () => {
        if (isListening) {
            stopDictation();
        }
        onClose();
    };

    if (!hazard) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { maxHeight: '80vh' }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <WarningIcon sx={{ color: 'warning.main', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" component="div" sx={{ lineHeight: 1.3 }}>
                            {isNewCustomHazard ? 'Add Custom Hazard' : (isEditMode ? 'Edit Actions' : 'Select Actions')}
                        </Typography>
                        {!isNewCustomHazard && (
                            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                                {hazardName}
                            </Typography>
                        )}
                        {(workActivity || energyName) && (
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                {workActivity && (
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>Activity:</strong> {workActivity}
                                    </Typography>
                                )}
                                {energyName && (
                                    <Typography variant="caption" color="text.secondary">
                                        <strong>Energy:</strong> {energyName}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {/* Custom Hazard Name Input */}
                {isNewCustomHazard && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Hazard Name *
                        </Typography>
                        <TextField
                            inputRef={customHazardInputRef}
                            value={customHazardName}
                            onChange={(e) => setCustomHazardName(e.target.value)}
                            placeholder="Enter hazard name..."
                            size="small"
                            fullWidth
                            autoFocus
                            error={!hasValidHazardName && customHazardName !== ''}
                            InputProps={{
                                endAdornment: dictationSupported && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => toggleDictation('hazardName')}
                                            color={isListening && activeField === 'hazardName' ? 'error' : 'default'}
                                            size="small"
                                            title={isListening && activeField === 'hazardName' ? 'Stop recording' : 'Start voice input'}
                                        >
                                            {isListening && activeField === 'hazardName' ? <MicOffIcon /> : <MicIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {isListening && activeField === 'hazardName' && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                Recording... Speak clearly
                            </Typography>
                        )}
                        <Divider sx={{ mt: 2 }} />
                    </Box>
                )}

                {/* Actions Section */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Actions {allActions.length > 0 && `(${selectedActionIds.size} selected)`} *
                </Typography>

                {allActions.length > 0 ? (
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 2 }}>
                        {allActions.map((action) => (
                            <Box
                                key={action.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    bgcolor: action.isCustom ? 'primary.50' : 'transparent',
                                    border: action.isCustom ? '1px solid' : 'none',
                                    borderColor: action.isCustom ? 'primary.200' : 'transparent',
                                    mb: 0.5,
                                    '&:hover': { bgcolor: action.isCustom ? 'primary.100' : 'action.hover' }
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedActionIds.has(action.id)}
                                            onChange={() => handleActionToggle(action.id)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2">
                                                {action.value || action.name}
                                            </Typography>
                                            {action.isCustom && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        bgcolor: 'primary.main',
                                                        color: 'white',
                                                        px: 0.75,
                                                        py: 0.125,
                                                        borderRadius: 0.5,
                                                        fontSize: '10px',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    ADDED
                                                </Typography>
                                            )}
                                        </Stack>
                                    }
                                    sx={{ flex: 1, m: 0 }}
                                />
                                {action.isCustom && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveCustomAction(action.id)}
                                        sx={{ ml: 1 }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {isNewCustomHazard
                            ? 'Add at least one action for this hazard below.'
                            : 'No predefined actions for this hazard. Please add custom actions below.'}
                    </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Add Custom Action
                </Typography>

                <Stack direction="row" spacing={1} alignItems="stretch">
                    <TextField
                        inputRef={customActionInputRef}
                        value={customActionText}
                        onChange={(e) => setCustomActionText(e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, 'addAction')}
                        placeholder="Type or dictate custom action..."
                        size="small"
                        fullWidth
                        InputProps={{
                            endAdornment: dictationSupported && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => toggleDictation('actionText')}
                                        color={isListening && activeField === 'actionText' ? 'error' : 'default'}
                                        size="small"
                                        title={isListening && activeField === 'actionText' ? 'Stop recording' : 'Start voice input'}
                                    >
                                        {isListening && activeField === 'actionText' ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={handleAddCustomAction}
                        disabled={!customActionText.trim()}
                        sx={{
                            minWidth: 40,
                            px: 1.5,
                            height: 40
                        }}
                    >
                        <AddIcon />
                    </Button>
                </Stack>

                {isListening && activeField === 'actionText' && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        Recording... Speak clearly
                    </Typography>
                )}

                {!isValid && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {!hasValidHazardName
                            ? 'Please enter a hazard name'
                            : 'Please select at least one action or add a custom action'}
                    </Alert>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!isValid}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
}
